import { useCallback, useEffect, useState } from "react";
import {
    Alert,
    Button,
    Card,
    Col,
    Container,
    Form,
    OverlayTrigger,
    Row,
    Spinner,
    Tooltip,
} from "react-bootstrap";
import { Helmet } from "react-helmet-async";
import { getSapUnpaidInvoiceCompanies } from "./hooks/api";
import { useHandleGenerateSoa } from "./hooks/useGenerateSoa";
import { useNavigate } from "react-router-dom";
import { ArrowLeft } from "react-feather";
import { useFetchData } from "../../../../../hooks/useFetchData";

const emptyCompanyRow = () => ({
    id: crypto.randomUUID(),
    bp_name: "",
    bp_code: "",
    remarks: "",
});

const GenerateSoa = () => {
    const navigate = useNavigate();
    const [rows, setRows] = useState([emptyCompanyRow()]);
    const { generate, loading, error, setError } = useHandleGenerateSoa();
    const {data: companies, fetchData: fetchCompanies} = useFetchData(getSapUnpaidInvoiceCompanies)

   

    const handleGenerate = async () => {
        const validRows = rows.filter((row) => row.bp_code && row.bp_name);

        if (validRows.length === 0) {
            setError("Please select at least one company!");
            return;
        }

        await generate({
            companies: validRows.map(({ bp_code, bp_name, remarks }) => ({
                bp_code,
                bp_name,
                remarks,
            })),
        });
    };

    const handleAddCompany = () => {
        setRows((prev) => [...prev, emptyCompanyRow()]);
    };

    const handleRemoveCompany = (id) => {
        setRows((prev) => prev.filter((row) => row.id !== id));
    };

    const handleRowChange = (id, field, value) => {
        setRows((prev) =>
            prev.map((row) => {
                if (row.id !== id) return row;

                if (field === "bp_code") {
                    const selected = companies.find((c) => c.bp_code === value);
                    return {
                        ...row,
                        bp_code: value,
                        bp_name: selected?.bp_name ?? "",
                    };
                }

                return { ...row, [field]: value };
            })
        );
    };

    useEffect(() => {
        fetchCompanies();
    }, [fetchCompanies]);
    return (
        <>
            <Helmet title="Generate Statement of Account" />
            <Container fluid>
                <div className="d-flex justify-content-between align-items-center mb-3">
                    <h1 className="h3 mb-0">Generate Statement of Account</h1>

                    <OverlayTrigger
                        placement="top"
                        overlay={<Tooltip>Return</Tooltip>}
                    >
                        <Button
                            className="ms-2 "
                            onClick={() =>
                                navigate("/tms/sap-unpaid-invoice-uploads")
                            }
                            disabled={loading}
                        >
                            <ArrowLeft className="feather" />

                            {loading && (
                                <Spinner
                                    animation="border"
                                    size="sm"
                                    className="ms-2"
                                />
                            )}
                        </Button>
                    </OverlayTrigger>
                </div>
                <Card>
                    <Card.Header className="d-flex justify-content-between align-items-center">
                        <h1 className="mb-0">Companies</h1>

                        <Button onClick={handleGenerate} disabled={loading}>
                            {loading ? (
                                <>
                                    <Spinner size="sm" className="me-2" />
                                    Generating...
                                </>
                            ) : (
                                "Download SOA"
                            )}
                        </Button>
                    </Card.Header>
                    <Card.Body>
                        {rows.map((row, index) => (
                            <Row key={row.id} className="mb-3 align-items-end">
                                <Col md={5}>
                                    <Form.Label>Company</Form.Label>
                                    <Form.Select
                                        value={row.bp_code}
                                        onChange={(e) =>
                                            handleRowChange(
                                                row.id,
                                                "bp_code",
                                                e.target.value
                                            )
                                        }
                                    >
                                        <option value={""}>
                                            Select Company ...
                                        </option>
                                        {companies.map((c) => (
                                            <option
                                                key={c.bp_code}
                                                value={c.bp_code}
                                            >
                                                {c.bp_name}
                                            </option>
                                        ))}
                                    </Form.Select>
                                </Col>
                                <Col md={6}>
                                    <Form.Label>Remarks (Optional)</Form.Label>
                                    <Form.Control
                                        value={row.remarks}
                                        onChange={(e) =>
                                            handleRowChange(
                                                row.id,
                                                "remarks",
                                                e.target.value
                                            )
                                        }
                                        as={"textarea"}
                                        rows={1}
                                    />
                                </Col>
                                <Col md={1}>
                                    {rows.length > 1 && (
                                        <Button
                                            variant="outline-danger"
                                            size="sm"
                                            onClick={() =>
                                                handleRemoveCompany(row.id)
                                            }
                                        >
                                            &times;
                                        </Button>
                                    )}
                                </Col>
                            </Row>
                        ))}
                        <Button variant="primary" onClick={handleAddCompany}>
                            + Add Company
                        </Button>

                        <hr />
                        {error && (
                            <Alert className="p-2" variant="danger">
                                {error}
                            </Alert>
                        )}
                    </Card.Body>
                </Card>
            </Container>
        </>
    );
};

export default GenerateSoa;
